/* One HTML entry owns each event; the calendar is progressive enhancement. */
(function () {
  'use strict';
  const ZONE = 'Europe/Berlin';
  const berlinParts = new Intl.DateTimeFormat('en-CA', {
    timeZone: ZONE, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
  });
  const pad = n => String(n).padStart(2, '0');
  function localParts(date) {
    return Object.fromEntries(berlinParts.formatToParts(date).filter(p => p.type !== 'literal').map(p => [p.type, p.value]));
  }
  function dateKey(date) {
    const p = localParts(date);
    return `${p.year}-${p.month}-${p.day}`;
  }
  function parseBerlinInstant(value) {
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\+(?:01|02):00$/.test(value)) throw new Error('Datum benötigt Sekunden und den Berliner UTC-Offset +01:00 oder +02:00.');
    const d = new Date(value);
    if (!Number.isFinite(d.getTime())) throw new Error('Ungültiger Terminzeitpunkt.');
    const p = localParts(d);
    if (`${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}:${p.second}` !== value.slice(0, 19)) throw new Error('Ungültiges Datum oder falscher UTC-Offset für Europe/Berlin.');
    if (+p.year < 2000 || +p.year > 2100) throw new Error('Unterstützter Datumsbereich: 2000 bis 2100.');
    return d;
  }
  function validateEvent(event) {
    if (!/^[a-z0-9][a-z0-9-]{0,79}$/.test(event.id || '')) throw new Error('Termin-ID fehlt oder enthält ungültige Zeichen.');
    if (!event.title?.trim() || !event.description?.trim() || !event.location?.trim()) throw new Error('Titel, Beschreibung und Ort sind erforderlich.');
    if (!['magic', '40k', 'aos', 'killteam', 'tabletop'].includes(event.game)) throw new Error('Unbekanntes Spielsystem.');
    if (!['scheduled', 'cancelled', 'rescheduled'].includes(event.status)) throw new Error('Unbekannter Terminstatus.');
    if (typeof event.example !== 'boolean') throw new Error('Beispielkennzeichnung fehlt.');
    if (/^\d{4}-\d{2}-\d{2}$/.test(event.start) && /^\d{4}-\d{2}-\d{2}$/.test(event.end)) {
      const start = new Date(event.start + 'T00:00:00Z'), end = new Date(event.end + 'T00:00:00Z');
      if (!Number.isFinite(+start) || !Number.isFinite(+end) || start.toISOString().slice(0,10) !== event.start || end.toISOString().slice(0,10) !== event.end || +end - +start !== 86400000 || +event.start.slice(0,4) < 2000 || +event.start.slice(0,4) > 2100) throw new Error('Ungültiger Datumsmerker.');
      return event;
    }
    if (event.openEnd === true) {
      parseBerlinInstant(event.start);
      if (event.end) throw new Error('Open End darf keine feste Endzeit enthalten.');
      return event;
    }
    const start = parseBerlinInstant(event.start), end = parseBerlinInstant(event.end);
    if (end <= start) throw new Error('Das Ende muss nach dem Beginn liegen.');
    if (event.start.slice(0, 10) !== event.end.slice(0, 10)) throw new Error('Mehrtagestermine bitte als einzelne Tagesveranstaltungen pflegen.');
    return event;
  }
  function shiftMonth(year, month, delta) {
    const d = new Date(Date.UTC(year, month + delta, 1));
    return [d.getUTCFullYear(), d.getUTCMonth()];
  }
  function monthCells(year, month) {
    const first = new Date(Date.UTC(year, month, 1));
    const before = (first.getUTCDay() + 6) % 7;
    const days = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    const cells = Array(before).fill(null);
    for (let day = 1; day <= days; day++) cells.push(`${year}-${pad(month + 1)}-${pad(day)}`);
    while (cells.length % 7) cells.push(null);
    return cells;
  }
  function foldLine(line) {
    let out = '', bytes = 0;
    const encoder = new TextEncoder();
    for (const character of line) {
      const size = encoder.encode(character).length;
      if (bytes + size > 75) { out += '\r\n '; bytes = 1; }
      out += character;
      bytes += size;
    }
    return out;
  }
  const escapeICS = value => String(value).replace(/\\/g, '\\\\').replace(/\r\n|\r|\n/g, '\\n').replace(/;/g, '\\;').replace(/,/g, '\\,');
  const utcICS = value => new Date(value).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');
  function toICS(input) {
    const e = validateEvent(input);
    const prefix = e.example ? 'BEISPIEL – ' : '';
    const note = e.example ? 'BEISPIELTERMIN – kein bestätigtes Event.\n' : '';
    const lines = [
      'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Tabletop Kontor Heins//Events//DE', 'CALSCALE:GREGORIAN',
      'BEGIN:VEVENT', `UID:${e.example ? 'example-' : ''}${e.id}@tabletopkontorheins.de`, `DTSTAMP:${utcICS(new Date())}`,
      ...(e.start.length === 10 ? [`DTSTART;VALUE=DATE:${e.start.replaceAll('-', '')}`, `DTEND;VALUE=DATE:${e.end.replaceAll('-', '')}`, 'TRANSP:TRANSPARENT'] : [`DTSTART:${utcICS(e.start)}`, ...(e.openEnd ? [] : [`DTEND:${utcICS(e.end)}`]) ]), `SUMMARY:${escapeICS(prefix + e.title)}`,
      `DESCRIPTION:${escapeICS(note + e.description + '\nEinmaliger Export. Änderungen werden nicht automatisch übernommen. Bitte vor dem Besuch den aktuellen Termin prüfen.')}`,
      `LOCATION:${escapeICS(e.location)}`, `STATUS:${e.status === 'cancelled' ? 'CANCELLED' : 'CONFIRMED'}`,
      'END:VEVENT', 'END:VCALENDAR'
    ];
    return lines.map(foldLine).join('\r\n') + '\r\n';
  }

  function isPast(event, now = new Date()) {
    if (event.openEnd) return dateKey(now) > event.start.slice(0, 10);
    return event.end.length === 10 ? dateKey(now) >= event.end : new Date(event.end) < now;
  }

  function init(root) {
    if (root.dataset.ready) return;
    const q = selector => root.querySelector(selector);
    const entries = Array.from(root.querySelectorAll('[data-event]'));
    const tools = q('[data-tools]'), list = q('[data-events-list]'), monthHost = q('[data-month-grid]');
    const error = q('[data-calendar-error]');
    try {
      const seen = new Set();
      const events = entries.map(el => {
        const start = el.querySelector('[data-start]'), end = el.querySelector('[data-end]');
        const example = el.dataset.example;
        if (!['true', 'false'].includes(example)) throw new Error('data-example muss true oder false sein.');
        const event = validateEvent({
          id: el.id, title: el.querySelector('[data-title]')?.textContent.trim(),
          description: el.querySelector('[data-description]')?.textContent.trim(),
          location: el.querySelector('[data-location]')?.textContent.trim(),
          start: start?.dateTime, end: end?.dateTime, openEnd: el.dataset.openEnd === 'true', game: el.dataset.game,
          status: el.dataset.status, example: example === 'true', el
        });
        if (seen.has(event.id)) throw new Error('Doppelte Termin-ID: ' + event.id);
        seen.add(event.id);
        return event;
      }).sort((a, b) => new Date(a.start) - new Date(b.start));
      root.dataset.ready = 'true';
      let year, month, game = 'all';
      let view = window.matchMedia('(min-width: 1000px)').matches ? 'month' : 'list';
      const today = () => dateKey(new Date());
      const setToday = () => { [year, month] = today().split('-').slice(0, 2).map(Number); month--; };
      setToday();
      const monthLabel = () => new Intl.DateTimeFormat('de-DE', {month: 'long', year: 'numeric', timeZone: 'UTC'}).format(new Date(Date.UTC(year, month, 1)));
      const dateLabel = value => new Intl.DateTimeFormat('de-DE', {weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: ZONE}).format(new Date(value));
      function download(e) {
        const blob = new Blob([toICS(e)], {type: 'text/calendar;charset=utf-8'});
        const url = URL.createObjectURL(blob), link = document.createElement('a');
        link.href = url; link.download = `${e.example ? 'beispiel-' : ''}${e.id}.ics`;
        root.append(link); link.click(); link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 10000);
      }
      events.forEach(e => {
        list.append(e.el);
        const status = e.el.querySelector('[data-time-state]');
        const past = isPast(e);
        status.textContent = past ? 'Vergangen' : '';
        status.hidden = !past;
        if (e.status !== 'cancelled') {
          const button = document.createElement('button');
          button.type = 'button'; button.className = 'tkhe-button tkhe-button--primary';
          button.textContent = e.example ? 'Beispieltermin speichern (.ics)' : (e.start.length === 10 ? 'Datum vormerken (.ics) – Uhrzeit folgt' : 'Termin speichern (.ics)');
          button.addEventListener('click', () => download(e));
          e.el.querySelector('[data-export]').append(button);
        }
      });
      function render() {
        const prefix = `${year}-${pad(month + 1)}`;
        const current = events.filter(e => e.start.startsWith(prefix) && (game === 'all' || game === e.game));
        q('[data-month-title]').textContent = monthLabel();
        q('[data-result-count]').textContent = `${current.length} ${current.length === 1 ? 'Termin' : 'Termine'}${current.some(e => e.example) ? ' · mit Beispieldaten' : ''}`;
        q('[data-prev]').disabled = year === 2000 && month === 0;
        q('[data-next]').disabled = year === 2100 && month === 11;
        q('[data-list-view]').setAttribute('aria-pressed', String(view === 'list'));
        q('[data-month-view]').setAttribute('aria-pressed', String(view === 'month'));
        for (const e of events) e.el.hidden = !current.includes(e);
        list.hidden = view !== 'list';
        monthHost.hidden = view !== 'month';
        const empty = q('[data-empty]');
        empty.hidden = current.length !== 0;
        q('[data-empty-text]').textContent = game === 'all' ? 'Für diesen Monat sind keine Termine eingetragen.' : 'Für dieses Spiel sind in diesem Monat keine Termine eingetragen. Wähle ein anderes Spiel oder einen anderen Monat.';
        q('[data-all-games]').hidden = game === 'all';
        const table = document.createElement('table'); table.className = 'tkhe-month';
        const caption = table.createCaption(); caption.className = 'tkhe-sr'; caption.textContent = `Eventkalender ${monthLabel()}`;
        const head = table.createTHead().insertRow();
        ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'].forEach(day => {
          const th = document.createElement('th'); th.scope = 'col';
          const abbr = document.createElement('abbr'); abbr.title = day; abbr.textContent = day.slice(0, 2); th.append(abbr); head.append(th);
        });
        const body = table.createTBody(); let row;
        monthCells(year, month).forEach((key, index) => {
          if (index % 7 === 0) row = body.insertRow();
          const td = row.insertCell();
          if (!key) { td.className = 'tkhe-day-empty'; return; }
          const number = document.createElement('span'); number.className = 'tkhe-day-number'; number.textContent = Number(key.slice(8));
          if (key === today()) { td.classList.add('tkhe-day-today'); number.setAttribute('aria-label', `Heute, ${Number(key.slice(8))}.`); }
          td.append(number);
          current.filter(e => e.start.startsWith(key)).forEach(e => {
            const button = document.createElement('button'); button.type = 'button'; button.className = 'tkhe-month-event';
            button.dataset.game = e.game;
            const time = document.createElement('span'); time.className = 'tkhe-month-time'; time.textContent = e.start.length === 10 ? 'Uhrzeit folgt' : e.start.slice(11, 16);
            const title = document.createElement('span'); title.textContent = e.title;
            const state = document.createElement('span'); state.className = 'tkhe-month-state';
            state.textContent = [e.example ? 'Beispiel' : '', e.status === 'cancelled' ? 'Abgesagt' : e.status === 'rescheduled' ? 'Verschoben' : '', isPast(e) ? 'Vergangen' : ''].filter(Boolean).join(' · ');
            button.append(time, title, state);
            button.setAttribute('aria-label', `${e.title}, ${dateLabel(e.start)}, ${e.start.length === 10 ? 'Uhrzeit folgt' : e.start.slice(11, 16) + ' Uhr'}. ${state.textContent}. Details anzeigen.`);
            button.addEventListener('click', () => {
              view = 'list'; render(); e.el.open = true;
              e.el.querySelector('summary').focus({preventScroll: true});
              e.el.scrollIntoView({block: 'start', behavior: 'auto'});
            });
            td.append(button);
          });
        });
        monthHost.replaceChildren(table);
      }
      q('[data-prev]').addEventListener('click', () => { [year, month] = shiftMonth(year, month, -1); render(); });
      q('[data-next]').addEventListener('click', () => { [year, month] = shiftMonth(year, month, 1); render(); });
      q('[data-today]').addEventListener('click', () => { setToday(); render(); });
      q('[data-game-filter]').addEventListener('change', e => { game = e.target.value; render(); });
      q('[data-list-view]').addEventListener('click', () => { view = 'list'; render(); });
      q('[data-month-view]').addEventListener('click', () => { view = 'month'; render(); });
      q('[data-all-games]').addEventListener('click', () => { game = 'all'; q('[data-game-filter]').value = 'all'; render(); });
      tools.hidden = false; render();
      root.addEventListener('click', e => {
        const link = e.target.closest('a[href^="#"]');
        if (link && link.hash === '#tkhe-calendar') q('[data-month-title]').focus({preventScroll: true});
      });
    } catch (cause) {
      tools.hidden = true; monthHost.hidden = true; list.hidden = false;
      entries.forEach(el => { el.hidden = false; el.querySelector('[data-export]')?.replaceChildren(); });
      q('[data-empty]').hidden = true;
      error.hidden = false;
      error.textContent = `Die Kalenderansicht konnte nicht geladen werden. Alle Termine stehen unten als Liste. Hinweis für die Bearbeitung: ${cause.message}`;
    }
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = {monthCells, shiftMonth, parseBerlinInstant, validateEvent, toICS, foldLine, isPast};
  if (typeof document !== 'undefined') {
    const start = () => document.querySelectorAll('[data-tkhe-events]').forEach(init);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
    document.addEventListener('shopify:section:load', start);
  }
})();
